import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Appearance, type Stripe } from '@stripe/stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import type { AppConfig, CreateSubscriptionResult, CustomerInput, Plan, PricingResponse } from '../types';
import { BrandHeader } from './BrandHeader';
import { CustomerForm, emptyCustomerForm, type CustomerFormState } from './CustomerForm';
import { PaymentSection } from './PaymentSection';
import { PlanToggle } from './PlanToggle';
import { Spinner } from './Spinner';

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#e0301e',
    colorBackground: '#ffffff',
    colorText: '#15181e',
    colorTextSecondary: '#5b6470',
    colorDanger: '#d92d20',
    fontFamily: 'Helvetica Neue, Arial, sans-serif',
    borderRadius: '10px',
    spacingUnit: '4px',
  },
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function CheckoutPage() {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const customerId = query.get('customerId') || undefined;
  const queryPlan = query.get('plan');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [plan, setPlan] = useState<Plan>('annual');
  const [form, setForm] = useState<CustomerFormState>(emptyCustomerForm);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const [step, setStep] = useState<'details' | 'payment'>('details');
  // New customers must create an account; existing accounts skip credentials.
  const [requireCredentials, setRequireCredentials] = useState(true);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<CreateSubscriptionResult | null>(null);
  const [createdSignature, setCreatedSignature] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, pr] = await Promise.all([api.getConfig(), api.getPricing()]);
        if (cancelled) return;
        setConfig(cfg);
        setPricing(pr);
        setPlan(queryPlan === 'annual' || queryPlan === 'monthly' ? queryPlan : pr.defaultPlan);
        setStripePromise(loadStripe(cfg.publishableKey));

        if (customerId) {
          try {
            const cust = await api.getCustomer(customerId);
            if (!cancelled && cust.found) {
              setForm((f) => ({
                ...f,
                email: cust.email ?? '',
                firstName: cust.firstName ?? '',
                lastName: cust.lastName ?? '',
              }));
              // Existing app account -> no credentials step.
              setRequireCredentials(!cust.hasAccount);
            }
          } catch {
            /* 404 / unresolved -> new customer: keep the credentials step on */
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof ApiError ? e.message : 'Failed to load checkout.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, queryPlan]);

  const selectedPricing = pricing?.plans[plan];

  function buildCustomerInput(): CustomerInput {
    const t = (s: string) => s.trim() || undefined;
    return {
      email: form.email.trim(),
      firstName: t(form.firstName),
      lastName: t(form.lastName),
      address: {
        line1: t(form.line1),
        line2: t(form.line2),
        city: t(form.city),
        state: t(form.state),
        postal_code: t(form.postal_code),
        country: t(form.country),
      },
    };
  }

  async function handleContinue(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (!EMAIL_RE.test(form.email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (requireCredentials) {
      if (form.username.trim().length < 3) {
        setFormError('Choose a username (at least 3 characters).');
        return;
      }
      if (form.password.length < 8) {
        setFormError('Password must be at least 8 characters.');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setFormError('Passwords do not match.');
        return;
      }
    }

    const input = buildCustomerInput();
    const credentials = requireCredentials
      ? { username: form.username.trim(), password: form.password }
      : undefined;
    const signature = JSON.stringify({ plan, customerId, input, withCreds: requireCredentials });
    // Reuse the existing subscription/clientSecret if nothing relevant changed.
    if (subscription && signature === createdSignature) {
      setStep('payment');
      return;
    }

    setCreating(true);
    try {
      const result = await api.createSubscription({ plan, customerId, customer: input, credentials });
      setSubscription(result);
      setCreatedSignature(signature);
      setStep('payment');
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : 'Could not start checkout. Please try again.',
      );
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <BrandHeader />
        <div className="card">
          <Spinner label="Loading checkout…" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <BrandHeader />
        <div className="card">
          <div className="card-section">
            <div className="alert alert-error">{loadError}</div>
            <p className="muted mt-16">
              Make sure the checkout API is running (<code>npm run dev:backend</code>).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BrandHeader siteUrl={config?.brand.siteUrl} />
      <div className="card">
        <div className="card-section">
          <p className="eyebrow">Gorilla App Subscription</p>
          <h1 className="product-name">{selectedPricing?.productName ?? 'Gorilla Bow Subscription'}</h1>
          {selectedPricing?.features?.length ? (
            <ul className="features">
              {selectedPricing.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          ) : selectedPricing?.productDescription ? (
            <p className="product-desc">{selectedPricing.productDescription}</p>
          ) : null}
          {pricing ? (
            <PlanToggle pricing={pricing} selected={plan} onSelect={setPlan} disabled={step === 'payment'} />
          ) : null}
        </div>

        {step === 'details' ? (
          <form className="card-section" onSubmit={handleContinue}>
            <p className="eyebrow">Your details</p>
            <CustomerForm
              value={form}
              onChange={setForm}
              disabled={creating}
              showCredentials={requireCredentials}
            />
            {formError ? <div className="alert alert-error">{formError}</div> : null}
            <button type="submit" className="btn btn-primary mt-24" disabled={creating}>
              {creating ? 'Starting…' : 'Continue to payment'}
            </button>
            <div className="secure-note">🔒 Secured by Stripe · cards never touch our servers</div>
          </form>
        ) : (
          <div className="card-section">
            <div className="summary">
              <div>
                <strong>{selectedPricing?.productName}</strong>
                <div className="muted">{plan === 'annual' ? 'Annual' : 'Monthly'} billing</div>
              </div>
              <div className="total">
                {subscription?.pricing.displayAmount}
                <span className="interval">{subscription?.pricing.displayInterval}</span>
              </div>
            </div>

            {stripePromise && subscription ? (
              <div className="mt-24">
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret: subscription.clientSecret, appearance }}
                >
                  <PaymentSection subscription={subscription} onBack={() => setStep('details')} />
                </Elements>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <p className="footer-note">
        Cancel anytime. Questions? Contact{' '}
        <a href={`mailto:${config?.brand.supportEmail ?? 'support@gorillabow.com'}`}>
          {config?.brand.supportEmail ?? 'support@gorillabow.com'}
        </a>
        .
      </p>
    </div>
  );
}
